import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Building2, ArrowRight } from 'lucide-react';

export default function BusinessRegisterPage() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [form, setForm] = useState({
    legal_name: '', registration_number: '', country: '', address: '',
    tax_number: '', bank_name: '', bank_account: '', swift_bic: '',
    website: '', business_type: '', contact_person: '', contact_phone: '',
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/b2b/company/register', form);
      toast.success('Company submitted for verification');
      await refresh();
      nav('/business-portal');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] py-16 px-4">
      <div className="mx-auto max-w-3xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-xl bg-[hsl(var(--brand-gold))]/10 flex items-center justify-center"><Building2 className="h-6 w-6 text-[hsl(var(--brand-navy))]" /></div>
          <div>
            <h1 className="font-serif text-3xl text-[hsl(var(--brand-navy-2))]">Register your company</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Provide the legal details of your entity. Verification typically takes 1–2 business days.</p>
          </div>
        </div>
        <Card className="bg-white"><CardContent className="p-6">
          <form onSubmit={submit} className="space-y-4" data-testid="business-register-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Legal name *</Label><Input required data-testid="b2b-legal-name" value={form.legal_name} onChange={e => upd('legal_name', e.target.value)} /></div>
              <div><Label>Registration number *</Label><Input required data-testid="b2b-reg-number" value={form.registration_number} onChange={e => upd('registration_number', e.target.value)} /></div>
              <div><Label>Country *</Label><Input required data-testid="b2b-country" value={form.country} onChange={e => upd('country', e.target.value)} /></div>
              <div><Label>Tax / VAT number</Label><Input value={form.tax_number} onChange={e => upd('tax_number', e.target.value)} /></div>
              <div className="sm:col-span-2"><Label>Registered address *</Label><Input required data-testid="b2b-address" value={form.address} onChange={e => upd('address', e.target.value)} /></div>
              <div><Label>Business type</Label><Input value={form.business_type} onChange={e => upd('business_type', e.target.value)} placeholder="e.g. LLC, JSC, Ltd" /></div>
              <div><Label>Website</Label><Input value={form.website} onChange={e => upd('website', e.target.value)} /></div>
              <div><Label>Bank name</Label><Input value={form.bank_name} onChange={e => upd('bank_name', e.target.value)} /></div>
              <div><Label>Bank account (IBAN)</Label><Input value={form.bank_account} onChange={e => upd('bank_account', e.target.value)} /></div>
              <div><Label>SWIFT / BIC</Label><Input value={form.swift_bic} onChange={e => upd('swift_bic', e.target.value)} /></div>
              <div><Label>Contact person</Label><Input value={form.contact_person} onChange={e => upd('contact_person', e.target.value)} /></div>
              <div><Label>Contact phone</Label><Input value={form.contact_phone} onChange={e => upd('contact_phone', e.target.value)} /></div>
            </div>
            <Button type="submit" disabled={busy} data-testid="b2b-register-submit" className="w-full bg-[hsl(var(--brand-navy))]">Submit for verification <ArrowRight className="ml-2 h-4 w-4" /></Button>
          </form>
        </CardContent></Card>
      </div>
    </div>
  );
}
